import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLenis } from "lenis/react";

const EASE = [0.16, 1, 0.3, 1];

const paths = [
  {
    id: "riders",
    accent: "yellow",
    eyebrow: "For Riders",
    title: "Your ride, three taps away.",
    cta: "Get your first ride",
    target: "#rider-signup",
    testid: "riders-panel",
    ctaTestid: "riders-panel-cta",
    steps: [
      { n: "01", title: "Request", copy: "Set your pickup anywhere in Albany and get matched with a nearby driver in seconds." },
      { n: "02", title: "Ride", copy: "Track your driver live, hop in, and enjoy a comfortable trip across the Capital Region." },
      { n: "03", title: "Pay", copy: "Cashless, transparent pricing with fares built to beat the big apps. Rate and go." },
    ],
  },
  {
    id: "drivers",
    accent: "green",
    eyebrow: "For Drivers",
    title: "Your car, your schedule, your money.",
    cta: "Start earning today",
    target: "#driver-signup",
    testid: "drivers-panel",
    ctaTestid: "drivers-panel-cta",
    steps: [
      { n: "01", title: "Sign Up", copy: "A quick application with your license, insurance, and vehicle details. That's it." },
      { n: "02", title: "Get Approved", copy: "Fast background check and vehicle verification — most drivers are road-ready in days." },
      { n: "03", title: "Earn", copy: "Drive when you want and keep more of every fare. Weekly payouts, zero surprises." },
    ],
  },
];

function Step({ step, accent, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay: index * 0.12 }}
      className="group relative border-t border-white/10 pt-8 pb-10 flex gap-6 items-start"
      data-testid={`step-${accent}-${step.n}`}
    >
      <span
        className={`font-display font-bold text-7xl md:text-8xl leading-none select-none transition-colors duration-500 ${
          accent === "yellow" ? "text-outline group-hover:text-brand-yellow" : "text-outline group-hover:text-brand-green"
        }`}
      >
        {step.n}
      </span>
      <div>
        <h4 className="font-display font-semibold text-2xl md:text-3xl mb-3 tracking-tight">{step.title}</h4>
        <p className="text-zinc-400 leading-relaxed text-sm md:text-base max-w-sm">{step.copy}</p>
      </div>
    </motion.div>
  );
}

export default function HowItWorks() {
  const lenis = useLenis();
  const go = (e, href) => {
    e.preventDefault();
    if (lenis) lenis.scrollTo(href, { offset: -72, duration: 1.5 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative py-28 md:py-36" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-20 md:mb-28"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">How it works</p>
          <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Two roads in. <span className="text-zinc-500">One city moving.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
          {paths.map((path, pi) => (
            <motion.div
              key={path.id}
              id={path.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: EASE, delay: pi * 0.15 }}
              className="scroll-mt-28"
              data-testid={path.testid}
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    path.accent === "yellow" ? "bg-brand-yellow" : "bg-brand-green"
                  }`}
                />
                <span
                  className={`text-xs font-bold tracking-[0.3em] uppercase ${
                    path.accent === "yellow" ? "text-brand-yellow" : "text-brand-green"
                  }`}
                >
                  {path.eyebrow}
                </span>
              </div>
              <h3 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-10">
                {path.title}
              </h3>

              <div>
                {path.steps.map((s, i) => (
                  <Step key={s.n} step={s} accent={path.accent} index={i} />
                ))}
              </div>

              <a
                href={path.target}
                onClick={(e) => go(e, path.target)}
                data-testid={path.ctaTestid}
                className={`group inline-flex items-center gap-2 mt-2 text-sm font-bold uppercase tracking-[0.15em] border-b-2 pb-1 transition-colors duration-300 ${
                  path.accent === "yellow"
                    ? "text-brand-yellow border-brand-yellow/40 hover:border-brand-yellow"
                    : "text-brand-green border-brand-green/40 hover:border-brand-green"
                }`}
              >
                {path.cta}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
